import { useState, useEffect } from 'react';

// Deduplicates a single shared GET across every mounted consumer. The first
// mount triggers one fetch; later mounts reuse the cached result and the same
// in-flight promise, so a page with several consumers (sidebar card, gates,
// modals) makes one request instead of N. setData() updates the cache and
// notifies every live consumer, so a write in one component (e.g. dismissing a
// modal) is reflected everywhere without a refetch.
//
// Scope note: the cache lives for the life of the JS app instance (cleared on a
// full page reload). Like the per-component fetch it replaces, it does not poll
// or refetch — data is treated as stable within a page session, and mutations
// go through setData rather than a re-request.
export function createSharedResource(fetcher) {
  let cache = null;      // resolved data (null also means loaded-but-empty)
  let promise = null;    // in-flight/settled fetch promise
  let loaded = false;    // whether the fetch has completed at least once
  const subscribers = new Set();

  function load() {
    if (!promise) {
      promise = fetcher()
        .then(data => {
          cache = data;
          loaded = true;
          return data;
        })
        .catch(() => {
          loaded = true;
          return null;
        });
    }
    return promise;
  }

  function setData(updater) {
    cache = typeof updater === 'function' ? updater(cache) : updater;
    subscribers.forEach(fn => fn(cache));
  }

  function useResource() {
    const [data, setLocal] = useState(cache);
    const [loading, setLoading] = useState(!loaded);

    useEffect(() => {
      subscribers.add(setLocal);
      let active = true;
      load().then(resolved => {
        if (active) {
          setLocal(resolved);
          setLoading(false);
        }
      });
      return () => {
        active = false;
        subscribers.delete(setLocal);
      };
    }, []);

    return { data, loading };
  }

  return { useResource, setData };
}
